import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Avatar, Typography } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceChangeIcon from '@mui/icons-material/PriceChange';

// ─── nav structure ─────────────────────────────────────────────────────────────
const NAV = [
  {
    items: [
      { label: 'Dashboard',  path: '/mover-portal/dashboard',  icon: <DashboardIcon sx={{ fontSize: 20 }} /> },
      { label: 'Bookings',   path: '/mover-portal/bookings',   icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
      { label: 'Calendar', path: '/mover-portal/calendar', icon: <CalendarTodayIcon sx={{ fontSize: 16 }} /> },
      { label: 'Pricing',    path: '/mover-portal/pricing',    icon: <PriceChangeIcon sx={{ fontSize: 16 }} /> },
    ],
  },
  {
    items: [
      { label: 'Company',    path: '/mover-portal/profile',    icon: <BusinessIcon sx={{ fontSize: 16 }} /> },
      { label: 'Reviews',    path: '/mover-portal/reviews',    icon: <ReviewsIcon sx={{ fontSize: 16 }} /> },
      { label: 'Team',       path: '/mover-portal/team',       icon: <GroupIcon sx={{ fontSize: 16 }} /> },
    ],
  },
  {
    items: [
      { label: 'Payments', path: '/mover-portal/payments', icon: <AccountBalanceWalletIcon sx={{ fontSize: 16 }} /> },
    ],
  },
];

// ─── single nav item ───────────────────────────────────────────────────────────
function NavItem({ item, active, onNavigate }) {
  const base = {
    display: 'flex', alignItems: 'center', gap: 1,
    px: 1.15, py: .72,
    borderRadius: .75, cursor: 'pointer',
    textDecoration: 'none', color: 'inherit',
    transition: 'all .15s',
    position: 'relative',
    bgcolor: active ? '#008D7B' : 'transparent',
    '&:hover': { bgcolor: active ? '#008D7B' : '#E9F5F4' },
  };

  const inner = (
    <Box component={Link} to={item.path} onClick={onNavigate} sx={base}>
      {/* active indicator */}
      <Box sx={{ color: active ? '#FFFFFF' : '#60748D', flexShrink: 0, display: 'flex' }}>
        {item.icon}
      </Box>
      <Typography variant="body2" fontWeight={active ? 700 : 600} sx={{ color: active ? '#FFFFFF' : '#415A72', flex: 1, fontSize: 11 }}>
        {item.label}
      </Typography>
    </Box>
  );
  return inner;
}

// ─── main sidebar ──────────────────────────────────────────────────────────────
export default function MoverSidebar({ profile, onLogout, onNavigate }) {
  const location = useLocation();

  return (
    <Box sx={{
      width: 184, flexShrink: 0,
      height: 'calc(100vh - 58px)',
      bgcolor: '#F8FCFC',
      display: 'flex', flexDirection: 'column',
      borderRight: '1px solid #E0ECEE',
      overflow: 'hidden',
      position: 'fixed',
      top: 58,
      left: 0,
      zIndex: 1200,
    }}>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: 1.2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#1e293b', borderRadius: 2 },
      }}>
        {NAV.map((group) => (
          <Box key={group.items[0].path} sx={{ mb: 1.75 }}>
            {group.items.map(item => (
              <NavItem
                key={item.path}
                item={item}
                active={location.pathname === item.path || location.pathname + location.hash === item.path}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1.2, py: 1.25, borderTop: '1px solid #E0ECEE' }}>
        <NavItem
          item={{ label: 'Settings', path: '/mover-portal/settings', icon: <SettingsIcon sx={{ fontSize: 20 }} /> }}
          active={location.pathname === '/mover-portal/settings'}
          onNavigate={onNavigate}
        />

        {/* Logout */}
        <Box
          onClick={() => { onNavigate?.(); onLogout?.(); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.15, py: .8,
            borderRadius: .75, cursor: 'pointer',
            color: '#94a3b8',
            '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444' },
            transition: 'all .15s',
          }}
        >
          <LogoutIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11 }}>Log out</Typography>
        </Box>
      </Box>
    </Box>
  );
}
